import { CheckCircle2, Factory as FactoryIcon, FlaskConical, Cog, Package, Award, Truck } from "lucide-react";
import Footer from "@/components/Footer";

const IMAGES = {
  tanks: "https://images.pexels.com/photos/5532836/pexels-photo-5532836.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lab: "https://images.pexels.com/photos/15831822/pexels-photo-15831822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bottling: "https://images.unsplash.com/photo-1530037335614-e68828dcf258?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxib3R0bGluZyUyMGxpbmUlMjBiZXZlcmFnZSUyMGZhY3RvcnklMjBwcm9kdWN0aW9ufGVufDB8fHx8MTc3MTk5NDg1NXww&ixlib=rb-4.1.0&q=85",
  tanks2: "https://images.unsplash.com/photo-1744711372006-acc23b750590?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxzdGFpbmxlc3MlMjBzdGVlbCUyMHRhbmtzJTIwYnJld2VyeSUyMHdpbmVyeSUyMGZlcm1lbnRhdGlvbnxlbnwwfHx8fDE3NzE5OTQ4NjJ8MA&ixlib=rb-4.1.0&q=85",
  production: "https://images.unsplash.com/photo-1558419358-1d40b2a5d5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxib3R0bGluZyUyMGxpbmUlMjBiZXZlcmFnZSUyMGZhY3RvcnklMjBwcm9kdWN0aW9ufGVufDB8fHx8MTc3MTk5NDg1NXww&ixlib=rb-4.1.0&q=85",
  wine: "https://images.pexels.com/photos/4176427/pexels-photo-4176427.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const capabilities = [
  {
    icon: FlaskConical,
    title: "Laboratorio de Formulación",
    description: "Desarrollo de fórmulas propias con equipo de I+D especializado. Creamos recetas únicas y estables.",
  },
  {
    icon: Cog,
    title: "Pruebas Piloto",
    description: "Producción de lotes de prueba para validar fórmulas antes de la producción industrial.",
  },
  {
    icon: FactoryIcon,
    title: "Producción Industrial",
    description: "Líneas de producción automatizadas con capacidad para grandes volúmenes.",
  },
  {
    icon: Package,
    title: "Embotellado y Etiquetado",
    description: "Múltiples líneas de envasado para vidrio, PET, lata y bag in box.",
  },
  {
    icon: Award,
    title: "Control de Calidad",
    description: "Laboratorio de análisis interno que garantiza la calidad de cada lote producido.",
  },
  {
    icon: Truck,
    title: "Logística",
    description: "Gestión de almacenamiento y distribución desde nuestras instalaciones.",
  },
];

const stats = [
  { value: "5.000m²", label: "Instalaciones" },
  { value: "3", label: "Líneas de producción" },
  { value: "100K+", label: "Unidades/mes" },
  { value: "50+", label: "Proyectos realizados" },
];

export default function FactoryPage() {
  return (
    <main className="min-h-screen" data-testid="factory-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.tanks} alt="Factory tanks" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-udl-bg via-udl-bg/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-20">
          <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
            Infraestructura Industrial
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-udl-white mb-4 md:mb-6 max-w-3xl">
            Nuestra <span className="text-udl-primary">Fábrica</span>
          </h1>
          <p className="text-udl-gray-400 text-base md:text-lg max-w-2xl leading-relaxed">
            Unique Drinks Lab dispone de infraestructura industrial propia para el desarrollo 
            y producción de bebidas. Del laboratorio a la línea de producción, todo sucede 
            dentro de nuestras instalaciones.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-16 bg-udl-surface border-y border-udl-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-udl-primary font-heading font-bold text-3xl md:text-4xl lg:text-5xl mb-2">
                  {stat.value}
                </p>
                <p className="text-udl-gray-400 text-xs md:text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
              Capacidades
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white">
              Servicios de fábrica
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {capabilities.map((cap, i) => (
              <div key={i} className="card-feature">
                <cap.icon className="text-udl-primary mb-4" size={32} />
                <h3 className="text-udl-white font-heading font-bold text-lg mb-3">{cap.title}</h3>
                <p className="text-udl-gray-400 text-sm leading-relaxed">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-16 md:py-20 bg-udl-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
              Instalaciones
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-udl-white">
              Nuestras instalaciones
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group">
              <img src={IMAGES.tanks} alt="Depósitos de acero inoxidable" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Depósitos de fermentación</p>
              </div>
            </div>
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group relative">
              <img src={IMAGES.bottling} alt="Línea de embotellado" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Línea de embotellado</p>
              </div>
            </div>
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group relative">
              <img src={IMAGES.lab} alt="Laboratorio" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Laboratorio de I+D</p>
              </div>
            </div>
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group relative">
              <img src={IMAGES.tanks2} alt="Depósitos industriales" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Depósitos de almacenamiento</p>
              </div>
            </div>
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group relative">
              <img src={IMAGES.production} alt="Producción en línea" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Producción automatizada</p>
              </div>
            </div>
            <div className="aspect-video md:aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden group relative">
              <img src={IMAGES.wine} alt="Embotellado de vino" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-udl-white font-medium text-sm">Encajado y empaquetado</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
                Proceso
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white mb-6 md:mb-8">
                Control total del proceso productivo
              </h2>
              <div className="space-y-4 md:space-y-6">
                {[
                  "Desarrollo de fórmulas en laboratorio propio",
                  "Pruebas piloto para validación de producto",
                  "Producción industrial a gran escala",
                  "Embotellado automatizado multi-formato",
                  "Control de calidad en cada fase",
                  "Almacenamiento y logística integrada",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <CheckCircle2 className="text-udl-primary mt-0.5 flex-shrink-0" size={20} />
                    <span className="text-udl-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden">
                <img src={IMAGES.bottling} alt="Bottling line" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
